import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Search, Users2 } from "lucide-react";
import { fmtDate } from "@/lib/format";

/**
 * Modal universal de drill-down do Dashboard.
 * Renderiza a lista de colaboradores que compõem qualquer indicador.
 *
 * Props:
 *  - open         boolean
 *  - onClose      () => void
 *  - title        string   — título do modal
 *  - category     string   — total | vinculo | clt_ativos | clt_afastados | tipo_operacional
 *                             | afastamentos_tipo | afastamentos_bucket
 *                             | admitidos_mes | demitidos_mes
 *  - keyValue     string?  — filtro secundário (ex: 'CLT', 'AUXILIO_DOENCA')
 *  - companyId    string?
 *  - sectorId     string?
 *  - period       string?  (AAAA-MM)
 */
export default function HeadcountDrillDialog({
  open, onClose, title, category, keyValue,
  companyId, sectorId, period,
}) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !category) return;
    setLoading(true);
    setSearch("");
    const params = { category };
    if (keyValue) params.key = keyValue;
    if (companyId) params.company_id = companyId;
    if (sectorId) params.sector_id = sectorId;
    if (period) params.period = period;
    api.get("/headcount/drill", { params })
      .then(({ data }) => { setItems(data?.items || []); setCount(data?.count || 0); })
      .catch(() => { setItems([]); setCount(0); })
      .finally(() => setLoading(false));
  }, [open, category, keyValue, companyId, sectorId, period]);

  const filtered = items.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (r.nome || "").toLowerCase().includes(q) ||
      (r.matricula || "").toLowerCase().includes(q) ||
      (r.cargo || "").toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[900px] max-h-[80vh]" data-testid="headcount-drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users2 className="h-4 w-4 text-[#234935]" />
            {title || "Detalhamento"}
            <Badge variant="outline" className="ml-2">{count}</Badge>
          </DialogTitle>
          <DialogDescription>
            Lista rastreável — cada número do Dashboard corresponde a esses colaboradores.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input placeholder="Buscar por nome, matrícula ou cargo…"
                 value={search} onChange={(e) => setSearch(e.target.value)}
                 className="pl-8" data-testid="drill-search" />
        </div>

        <div className="overflow-auto max-h-[52vh] rounded-md border border-slate-200">
          {loading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-8" /><Skeleton className="h-8" /><Skeleton className="h-8" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-[13px] text-slate-500">
              Nenhum colaborador encontrado.
            </div>
          ) : (
            <Table data-testid="drill-table">
              <TableHeader className="bg-slate-50 sticky top-0">
                <TableRow>
                  <TableHead>Matrícula</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Vínculo</TableHead>
                  <TableHead>Situação</TableHead>
                  {(category === "afastamentos_tipo" || category === "afastamentos_bucket") && (
                    <>
                      <TableHead>Tipo Afastamento</TableHead>
                      <TableHead>Dias</TableHead>
                    </>
                  )}
                  {category === "admitidos_mes" && <TableHead>Admissão</TableHead>}
                  {category === "demitidos_mes" && <TableHead>Desligamento</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} data-testid={`drill-row-${r.id}`}>
                    <TableCell className="text-[12.5px] tabular-nums">{r.matricula || "—"}</TableCell>
                    <TableCell className="text-[13px] font-medium">{r.nome}</TableCell>
                    <TableCell className="text-[12.5px] text-slate-600">{r.cargo || "—"}</TableCell>
                    <TableCell><Badge variant="outline">{r.forma_contratacao || "—"}</Badge></TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        r.status === "afastado" ? "text-amber-700 border-amber-300" :
                        r.status === "ferias" ? "text-blue-700 border-blue-300" :
                        r.status === "desligado" ? "text-rose-700 border-rose-300" :
                        "text-emerald-700 border-emerald-300"
                      }>{r.status}</Badge>
                    </TableCell>
                    {(category === "afastamentos_tipo" || category === "afastamentos_bucket") && (
                      <>
                        <TableCell className="text-[12px]">{r.leave_tipo}</TableCell>
                        <TableCell className="text-[12.5px] font-semibold">
                          {r.leave_dias != null ? r.leave_dias : "—"}
                        </TableCell>
                      </>
                    )}
                    {category === "admitidos_mes" && (
                      <TableCell className="text-[12.5px]">{r.data_admissao ? fmtDate(r.data_admissao) : "—"}</TableCell>
                    )}
                    {category === "demitidos_mes" && (
                      <TableCell className="text-[12.5px]">{r.data_desligamento ? fmtDate(r.data_desligamento) : "—"}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
